import { RefObject, useLayoutEffect, useRef, useState, useMemo } from 'react'
import { debounce } from 'lodash'

export const useResizeObserver = (
  ref: RefObject<Element>,
  callback: (entries: ResizeObserverEntry[]) => void,
  options = { skipFirst: false } // * skipFirst: skip first time (onMount)
) => {
  const { skipFirst } = options
  const isFirst = useRef(skipFirst)

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (isFirst.current) {
        isFirst.current = false
      } else {
        callback(entries)
      }
    })
    const target = ref.current
    if (!target) return undefined

    resizeObserver.observe(target)
    return () => resizeObserver.unobserve(target)
  }, [ref, callback])
}

const isHorizontalOverflow = (element?: HTMLElement | null) => {
  if (!element) {
    return false
  }
  return element.scrollWidth > element.clientWidth
}

export const useHorizontalOverflowOnResize = (): [React.RefObject<HTMLDivElement>, boolean] => {
  const elementRef = useRef<HTMLDivElement>(null)
  const [isOverflow, setIsOverflow] = useState(false)

  const updateHorizontalOverflow = useMemo(
    () =>
      debounce(() => {
        if (isHorizontalOverflow(elementRef.current)) {
          setIsOverflow(true)
        } else {
          setIsOverflow(false)
        }
      }, 10),
    []
  )

  useResizeObserver(elementRef, updateHorizontalOverflow)

  return [elementRef, isOverflow]
}
